// src/components/plm/PastOrders.jsx
import styles from "../../styles/PLMApp.module.css";
import { useMemo, useState } from "react";

function toDateLabel(raw) {
  if (!raw) return "—";
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getBuyer(order) {
  return order?.Buyer?.name || "—";
}
function getBuyerLocation(order) {
  return order?.Buyer?.location || "—";
}
function getSupplier(order) {
  return order?.Suppliers?.[0]?.name || "—";
}
function getSupplierLocation(order) {
  return order?.Suppliers?.[0]?.location || "—";
}
function getConstruction(order) {
  return order?.fabricDefault || order?.construction || "—";
}
function getDelivery(order) {
  return order?.Suppliers?.[0]?.deliveryDate || order?.deliveryDate || null;
}
function getStatusLabel(order) {
  if (order?.cancelled) return "Cancelled";
  if (order?.archived) return "Archived";
  return "—";
}
function getStatusDate(order) {
  if (order?.cancelled) return order?.cancelledAt || null;
  if (order?.archived) return order?.archivedAt || null;
  return null;
}
function getStatusDateLabel(order) {
  if (order?.cancelled) return "Cancelled";
  if (order?.archived) return "Archived";
  return "";
}

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ order }) {
  const isCancelled = !!order?.cancelled;
  return (
    <div
      className={`${styles.statusBadge} ${isCancelled ? styles.statusBadgeCancelled : styles.statusBadgeArchived}`}
    >
      {getStatusLabel(order)}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PastOrders({ orders = [] }) {
  const [search, setSearch] = useState("");

  const pastOrders = useMemo(
    () => orders.filter((o) => o?.archived === true || o?.cancelled === true),
    [orders],
  );

  const archivedCount = useMemo(
    () => pastOrders.filter((o) => o?.archived).length,
    [pastOrders],
  );
  const cancelledCount = useMemo(
    () => pastOrders.filter((o) => o?.cancelled).length,
    [pastOrders],
  );

  const visibleOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pastOrders;
    return pastOrders.filter((order) =>
      [
        order.orderNumber,
        order.id,
        getBuyer(order),
        getSupplier(order),
        getConstruction(order),
        getStatusLabel(order),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [pastOrders, search]);

  return (
    <>
      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <div className={styles.dashGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Archived Orders</div>
          <div
            className={`${styles.statValue} ${styles.orderProfileStatGreen}`}
          >
            {archivedCount}
          </div>
          <div className={styles.statSub}>
            Successfully completed &amp; archived
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Cancelled Orders</div>
          <div className={styles.statValue} style={{ color: "#d07070" }}>
            {cancelledCount}
          </div>
          <div className={styles.statSub}>
            Orders terminated before completion
          </div>
        </div>
      </div>

      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <div className={styles.panel} style={{ marginBottom: 24 }}>
        <div className={styles.panelTitle}>Past Order Registry</div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by order id, buyer, supplier, construction, or status"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── Order list ──────────────────────────────────────────────────── */}
      {visibleOrders.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>📦</div>
          <div className={styles.emptyStateText}>
            {pastOrders.length === 0
              ? "No past orders yet. Archived or cancelled orders will appear here."
              : "No past orders match the current search."}
          </div>
        </div>
      ) : (
        <div
          className={styles.dashPanels}
          style={{ gridTemplateColumns: "1fr" }}
        >
          {visibleOrders.map((order) => {
            const isCancelled = !!order?.cancelled;

            return (
              <div
                key={order.id}
                className={`${styles.panel} ${styles.orderPanel} ${isCancelled ? styles.orderPanelCancelled : ""}`}
              >
                <div
                  className={styles.orderRow}
                  style={{
                    "--order-cols": "200px 1fr 1fr 0.8fr 0.8fr 120px 120px",
                  }}
                >
                  {/* Order number ─────────────────────────────────────── */}
                  <div
                    className={`${styles.orderCellFirst} ${isCancelled ? styles.orderCellFirstCancelled : ""}`}
                  >
                    <div className={styles.cellLabel}>Order</div>
                    <div
                      className={`${styles.orderNumberDisplay} ${isCancelled ? styles.orderNumberDisplayCancelled : ""}`}
                    >
                      {order.orderNumber || "—"}
                    </div>
                    <div className={styles.orderIdDisplay}>{order.id}</div>
                    {getStatusDate(order) && (
                      <div className={styles.orderStatusDate}>
                        {getStatusDateLabel(order)}{" "}
                        {toDateLabel(getStatusDate(order))}
                      </div>
                    )}
                  </div>

                  {/* Buyer ────────────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Buyer</div>
                    <div className={styles.cellValue}>{getBuyer(order)}</div>
                    <div className={styles.cellMeta}>
                      {getBuyerLocation(order)}
                    </div>
                  </div>

                  {/* Supplier ────────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Supplier</div>
                    <div className={styles.cellValue}>{getSupplier(order)}</div>
                    <div className={styles.cellMeta}>
                      {getSupplierLocation(order)}
                    </div>
                  </div>

                  {/* Construction ────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Construction</div>
                    <div className={styles.cellValueConstruction}>
                      {getConstruction(order)}
                    </div>
                  </div>

                  {/* Delivery ────────────────────────────────────────── */}
                  <div className={styles.orderCell}>
                    <div className={styles.cellLabel}>Delivery</div>
                    <div className={styles.cellValue}>
                      {toDateLabel(getDelivery(order))}
                    </div>
                    <div
                      className={
                        isCancelled
                          ? styles.deliveryStatusCancelled
                          : styles.deliveryStatusCompleted
                      }
                    >
                      {isCancelled ? "Cancelled" : "Completed"}
                    </div>
                  </div>

                  {/* Status badge ────────────────────────────────────── */}
                  <div
                    className={`${styles.orderCell} ${styles.orderCellSelect}`}
                  >
                    <div className={styles.cellLabel}>Status</div>
                    <StatusBadge order={order} />
                  </div>

                  {/* Documents ───────────────────────────────────────── */}
                  <div className={styles.orderCellDocs}>
                    <div className={styles.orderCellDocsLabel}>Documents</div>
                    <button
                      type="button"
                      className={styles.btnViewDocs}
                      onClick={() => {
                        // wired up when DocumentModal is ready
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
